import { trpc } from '@/shared/api/trpc';
import { webBuildInfo } from '@/shared/config';
import { joinCss } from '@/shared/lib/css-utils';
import { formatUtcDate } from '@/shared/lib/format-utc-date';
import { Modal, ModalHeader, Text } from '@/shared/ui';
import { resolveReleaseSync } from '../lib/release-sync';
import css from './release-info-modal.module.scss';

const DATE_PATTERN = 'EEEE, LLLL dd, yyyy';
const TIME_PATTERN = 'HH:mm';

type ReleaseInfoModalProps = {
  open: boolean;
  onClose: () => void;
};

// Release Info: the running web bundle's build beside the running API server's
// build (both stamped automatically at build/boot — git SHA + timestamp). In a
// single-commit monorepo the two should match; a drift badge flags when they
// don't (e.g. only one side was redeployed).
export const ReleaseInfoModal = ({ open, onClose }: ReleaseInfoModalProps) => {
  // Only fetch the API build info while the modal is open.
  const { data: health, isPending } = trpc.health.check.useQuery(undefined, {
    enabled: open,
  });

  const webSha = webBuildInfo.sha;
  const apiSha = health?.version ?? null;
  const sync = resolveReleaseSync(webSha, apiSha, isPending);

  return (
    <Modal onClose={onClose} open={open}>
      <ModalHeader onClose={onClose} title="Release Info" />

      <div className={css.body}>
        <div className={css.grid}>
          <span className={css.label}>Type</span>
          <span className={css.colHead}>Web build</span>
          <span className={css.colHead}>API build</span>

          <span className={css.label}>Version</span>
          <span className={css.value}>{webSha}</span>
          <span className={css.value}>{apiSha ?? '—'}</span>

          <span className={css.label}>Date</span>
          <span className={css.value}>
            {formatUtcDate(webBuildInfo.buildTime, DATE_PATTERN)}
          </span>
          <span className={css.value}>
            {formatUtcDate(health?.buildTime, DATE_PATTERN)}
          </span>

          <span className={css.label}>Time</span>
          <span className={css.value}>
            {formatUtcDate(webBuildInfo.buildTime, TIME_PATTERN)}
          </span>
          <span className={css.value}>
            {formatUtcDate(health?.buildTime, TIME_PATTERN)}
          </span>
        </div>

        {sync.state !== 'unknown' ? (
          <Text
            className={joinCss(
              css.badge,
              sync.state === 'in-sync' ? css.badgeOk : css.badgeDrift
            )}
            size="sm"
          >
            {sync.state === 'in-sync'
              ? '✓ Web and API are on the same build'
              : '⚠ Web and API are on different builds'}
          </Text>
        ) : null}
      </div>
    </Modal>
  );
};
